// Scenario 1: healthy_monthly_us
// Mid-size US tenant, PROFESSIONAL x 8 seats, USD monthly. LAUNCH20 applied.
// AI credits: 8000 allocated, 3200 used, 500 top-up balance. Visa 4242.

import {
  buildConsumptionEvents,
  buildFeatureBreakdown
} from './ai-credit-usage-fixtures';
import { Scenario, TENANT_ID_DEFAULT, daysFromNow } from './scenario.types';
import { SHARED_PLANS, SHARED_TOP_UP_PACKS } from './shared-plans';

const PRICE_PER_SEAT_MINOR = 4000;   // PROFESSIONAL USD monthly
const SEATS = 8;
const SUBTOTAL_MINOR = PRICE_PER_SEAT_MINOR * SEATS;   // 32000
const DISCOUNT_MINOR = Math.round(SUBTOTAL_MINOR * 0.2); // 6400
const TOTAL_MINOR = SUBTOTAL_MINOR - DISCOUNT_MINOR;   // 25600

export const healthyMonthlyUs: Scenario = {
  name: 'healthy_monthly_us',
  subscription: {
    id: 'sub_healthy_monthly_us',
    stripeSubscriptionId: null,
    tenantId: TENANT_ID_DEFAULT,
    status: 'active',
    tier: 'PROFESSIONAL',
    billingPeriod: 'monthly',
    currency: 'USD',
    countryCode: 'US',
    seats: SEATS,
    pricePerSeatMinor: PRICE_PER_SEAT_MINOR,
    subtotalMinor: SUBTOTAL_MINOR,
    discountMinor: DISCOUNT_MINOR,
    taxMinor: 0,
    totalMinor: TOTAL_MINOR,
    appliedCoupon: {
      promotionCode: 'LAUNCH20',
      couponId: 'coupon_launch20',
      name: '20% off first 3 months',
      discountType: 'percent',
      percentOff: 20,
      amountOffMinor: null,
      duration: 'repeating',
      durationInMonths: 3,
      appliedAt: daysFromNow(-30),
      expiresAt: daysFromNow(60)   // appliedAt + 3 months
    },
    currentPeriodStart: daysFromNow(-2),
    currentPeriodEnd: daysFromNow(28),
    cancelAtPeriodEnd: false,
    canceledAt: null,
    pastDueSince: null,
    gracePeriodEndsAt: null
  },
  plans: SHARED_PLANS,
  invoices: [
    {
      id: 'inv_us_001',
      number: 'INV-2026-0040',
      status: 'paid',
      issuedAt: daysFromNow(-32),
      paidAt: daysFromNow(-32),
      periodStart: daysFromNow(-62),
      periodEnd: daysFromNow(-32),
      currency: 'USD',
      subtotalMinor: SUBTOTAL_MINOR,
      discountMinor: DISCOUNT_MINOR,
      taxMinor: 0,
      taxRate: null,
      taxBasis: null,
      totalMinor: TOTAL_MINOR,
      hostedInvoiceUrl: 'https://invoice.stripe.com/mock/inv_us_001',
      invoicePdfUrl: 'https://invoice.stripe.com/mock/inv_us_001.pdf',
      lineItems: [
        {
          description: 'Professional plan x 8 seats',
          kind: 'subscription',
          quantity: SEATS,
          unitAmountMinor: PRICE_PER_SEAT_MINOR,
          amountMinor: SUBTOTAL_MINOR,
          periodStart: daysFromNow(-62),
          periodEnd: daysFromNow(-32)
        }
      ]
    },
    {
      id: 'inv_us_002',
      number: 'INV-2026-0041',
      status: 'paid',
      issuedAt: daysFromNow(-2),
      paidAt: daysFromNow(-2),
      periodStart: daysFromNow(-2),
      periodEnd: daysFromNow(28),
      currency: 'USD',
      subtotalMinor: SUBTOTAL_MINOR,
      discountMinor: DISCOUNT_MINOR,
      taxMinor: 0,
      taxRate: null,
      taxBasis: null,
      totalMinor: TOTAL_MINOR,
      hostedInvoiceUrl: 'https://invoice.stripe.com/mock/inv_us_002',
      invoicePdfUrl: 'https://invoice.stripe.com/mock/inv_us_002.pdf',
      lineItems: [
        {
          description: 'Professional plan x 8 seats',
          kind: 'subscription',
          quantity: SEATS,
          unitAmountMinor: PRICE_PER_SEAT_MINOR,
          amountMinor: SUBTOTAL_MINOR,
          periodStart: daysFromNow(-2),
          periodEnd: daysFromNow(28)
        }
      ]
    }
  ],
  receipts: [
    {
      id: 'rcpt_us_001',
      chargedAt: daysFromNow(-32),
      amountMinor: TOTAL_MINOR,
      currency: 'USD',
      description: 'Solo subscription renewal',
      invoiceId: 'inv_us_001',
      topUpPackId: null
    },
    {
      id: 'rcpt_us_002',
      chargedAt: daysFromNow(-2),
      amountMinor: TOTAL_MINOR,
      currency: 'USD',
      description: 'Solo subscription renewal',
      invoiceId: 'inv_us_002',
      topUpPackId: null
    },
    {
      id: 'rcpt_us_topup_001',
      chargedAt: daysFromNow(-10),
      amountMinor: 2000,
      currency: 'USD',
      description: 'AI credit top-up: 2,000 credits',
      invoiceId: null,
      topUpPackId: 'topup-2k'
    }
  ],
  paymentMethod: {
    id: 'pm_us_001',
    type: 'card',
    brand: 'visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2030,
    isDefault: true
  },
  aiCreditBalance: {
    tenantId: TENANT_ID_DEFAULT,
    // S8: static ISO dates so the toast dedup key is stable across midnight UTC.
    billingCycleStart: '2026-05-21T00:00:00.000Z',
    billingCycleEnd: '2026-06-20T00:00:00.000Z',
    allocatedCredits: 8000,
    usedCredits: 3200,
    reservedCredits: 0,
    topUpBalance: 500,
    remainingCredits: 8000 - 3200 + 500,
    basicTurnsAllocated: 8000,
    basicTurnsUsed: 1500
  },
  topUpPacks: SHARED_TOP_UP_PACKS,
  // 8 active users on a tenant with 8 seats. Selecting 5 seats in the change-
  // plan flow triggers SEATS_BELOW_ACTIVE_USERS (5 < 8).
  activeUserCount: 8,
  // ---- #08: AI credit usage ------------------------------------------------
  // 22 events distributed across the cycle. Mix of all 4 buckets, weighted
  // toward ai_chat (~55%) and document_extraction (~27%). Credits per event
  // scale to land on the wallet's 3,200 used.
  consumptionEvents: buildConsumptionEvents({
    cycleAllocated: 8000,
    currentUsed: 3200,
    topUpBalance: 500,
    idPrefix: 'healthy_us',
    seeds: [
      { featureKey: 'ai_chat', creditsConsumed: 180, modelTier: 'premium', hoursAgo: 118, requestSummary: "Chat: 'kickoff plan'" },
      { featureKey: 'ai_chat', creditsConsumed: 95, modelTier: 'premium', hoursAgo: 112, requestSummary: "Chat: 'CRM filter help'" },
      { featureKey: 'document_extraction', creditsConsumed: 220, modelTier: 'premium', hoursAgo: 104, requestSummary: 'Extract invoice fields' },
      { featureKey: 'ai_chat', creditsConsumed: 70, modelTier: 'basic', hoursAgo: 100, requestSummary: "Chat: 'pricing question'" },
      { featureKey: 'summarization', creditsConsumed: 60, modelTier: 'basic', hoursAgo: 96, requestSummary: 'Summarize order notes' },
      { featureKey: 'ai_chat', creditsConsumed: 140, modelTier: 'premium', hoursAgo: 90, requestSummary: "Chat: 'invoice question'" },
      { featureKey: 'image_generation', creditsConsumed: 180, modelTier: 'premium', hoursAgo: 84, requestSummary: 'Generate brand image' },
      { featureKey: 'ai_chat', creditsConsumed: 120, modelTier: 'premium', hoursAgo: 78, requestSummary: "Chat: 'lead follow-up'" },
      { featureKey: 'document_extraction', creditsConsumed: 200, modelTier: 'premium', hoursAgo: 72, requestSummary: 'Extract receipt details' },
      { featureKey: 'ai_chat', creditsConsumed: 80, modelTier: 'basic', hoursAgo: 66, requestSummary: "Chat: 'how do I…?'" },
      { featureKey: 'image_generation', creditsConsumed: 150, modelTier: 'premium', hoursAgo: 60, requestSummary: 'Generate banner' },
      { featureKey: 'ai_chat', creditsConsumed: 220, modelTier: 'premium', hoursAgo: 54, requestSummary: "Chat: 'pricing strategy'" },
      { featureKey: 'summarization', creditsConsumed: 70, modelTier: 'basic', hoursAgo: 48, requestSummary: 'Summarize quarterly report' },
      { featureKey: 'ai_chat', creditsConsumed: 90, modelTier: 'premium', hoursAgo: 40, requestSummary: "Chat: 'task triage'" },
      { featureKey: 'document_extraction', creditsConsumed: 180, modelTier: 'premium', hoursAgo: 34, requestSummary: 'Extract PO line items' },
      { featureKey: 'ai_chat', creditsConsumed: 110, modelTier: 'premium', hoursAgo: 28, requestSummary: "Chat: 'CRM filter help'" },
      { featureKey: 'ai_chat', creditsConsumed: 60, modelTier: 'basic', hoursAgo: 22, requestSummary: "Chat: 'quick tip'" },
      { featureKey: 'image_generation', creditsConsumed: 200, modelTier: 'premium', hoursAgo: 18, requestSummary: 'Generate logo concept' },
      { featureKey: 'ai_chat', creditsConsumed: 140, modelTier: 'premium', hoursAgo: 14, requestSummary: "Chat: 'invoice question'" },
      { featureKey: 'summarization', creditsConsumed: 70, modelTier: 'basic', hoursAgo: 10, requestSummary: 'Summarize call transcript' },
      { featureKey: 'document_extraction', creditsConsumed: 160, modelTier: 'premium', hoursAgo: 6, requestSummary: 'Extract contract terms' },
      { featureKey: 'ai_chat', creditsConsumed: 110, modelTier: 'premium', hoursAgo: 2, requestSummary: "Chat: 'follow-up draft'" }
    ]
  }),
  featureUsageBreakdown: [],
  topUpHistory: [
    {
      id: 'tu_001',
      creditTopUpId: 'tu_001',
      purchasedAt: daysFromNow(-10),
      packDisplayName: '500-credit pack',
      credits: 500,
      amountMinor: 1000,
      currency: 'USD',
      remaining: 500,
      status: 'completed'
    }
  ]
};

// Compute the feature breakdown once at module load from the event seeds so the
// numbers stay in lockstep with the events list above. We mutate the fixture
// (rather than wrapping in a function) because the existing fixture shape is a
// plain constant elsewhere in the codebase.
(healthyMonthlyUs.featureUsageBreakdown as unknown) = buildFeatureBreakdown(
  healthyMonthlyUs.consumptionEvents ?? []
);
